import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useSearch, useLocation } from "wouter";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { isActionableReferral } from "@/lib/job-referral";
import { Search, Plus, AlertCircle, X, Info, Bell } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { TagSubscriptionManager } from "@/components/tag-subscription-manager";
import { AnchoredHintBubble } from "@/components/welcome-banner";
import { useHintDismissed, HINT_KEYS } from "@/hooks/use-hint-dismissed";
import { TalentFilterDrawer } from "@/components/talent-filter-drawer";
import { TalentSearchBar } from "@/components/talent-search-bar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useListProfiles,
  getListProfilesQueryKey,
  useListDisciplines,
  getListDisciplinesQueryKey,
  useGetAlumniStats,
  getGetAlumniStatsQueryKey,
  useListJobs,
  getListJobsQueryKey,
  useListActiveJobTags,
  getListActiveJobTagsQueryKey,
  useGetMe,
  getGetMeQueryKey,
  useGetRecruiterMe,
  getGetRecruiterMeQueryKey,
  useGetProfile,
  getGetProfileQueryKey,
  useListSavedProfiles,
  getListSavedProfilesQueryKey,
  useListAlumniSavedProfiles,
  getListAlumniSavedProfilesQueryKey,
  useDismissBookmarkHint,
  useDismissRecruiterBookmarkHint,
  useGetMyTagSubscriptions,
  getGetMyTagSubscriptionsQueryKey,
} from "@workspace/api-client-react";
import ProfileCard from "@/components/profile-card";
import JobCard from "@/components/job-card";
import JobSafetyBanner from "@/components/job-safety-banner";
import DynamicHeader from "@/components/dynamic-header";
import AlumniValueProps from "@/components/alumni-value-props";
import AlumniActionPanel from "@/components/alumni-action-panel";
import TalentActionPanel from "@/components/talent-action-panel";
import RecruiterValueProps from "@/components/recruiter-value-props";
import { JobFilterDrawer } from "@/components/job-filter-drawer";
import { JobSearchBar } from "@/components/job-search-bar";
import { BANNER_VERSIONS } from "@/lib/banner-versions";
import { DIRECTORY_HERO, OPPORTUNITIES_HERO } from "@/lib/site-meta";

type Tab = "talent" | "opportunities";

const ALUMNI_BANNER_KEY = BANNER_VERSIONS.alumni.currentKey;
const RECRUITER_BANNER_KEY = BANNER_VERSIONS.recruiter.currentKey;
const HOME_SCROLL_KEY = "home:scrollY";
const JOBS_SCROLL_KEY = "home:jobsScrollY";

function readAlumniBannerDismissed(): boolean {
  try {
    return localStorage.getItem(ALUMNI_BANNER_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Reads the non-HttpOnly `carry_hap` cookie set by the server at auth time.
 * Returns true when the server has previously confirmed this browser has an
 * alumni profile, so we can show a neutral skeleton instead of the join CTA
 * during the brief /me loading window on page reload — without affecting
 * unauthenticated users who never receive this cookie.
 */
function readAlumniHintCookie(): boolean {
  try {
    return document.cookie.split(";").some((c) => c.trim().startsWith("carry_hap=1"));
  } catch {
    return false;
  }
}

/**
 * Reads the non-HttpOnly `carry_hrp` cookie set by the server when a recruiter
 * session is established. Mirrors the same rendering-hint pattern as
 * `carry_hap` — lets the page suppress the unauthenticated hero flash for
 * returning recruiters before the /recruiter-me fetch completes.
 */
function readRecruiterHintCookie(): boolean {
  try {
    return document.cookie.split(";").some((c) => c.trim().startsWith("carry_hrp=1"));
  } catch {
    return false;
  }
}

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  missing_code: "Login failed — no authorization code was received. Please try again.",
  missing_state: "Login failed — the request was malformed. Please try again.",
  invalid_state: "Login failed — the request could not be verified. This may be a security issue; please try again.",
  token_exchange_failed: "Login failed — the request was declined. Please try again.",
  network_error: "Login failed — a network error occurred. Please try again.",
  discord_fetch_failed: "Login failed — we could not retrieve your Discord profile. Please try again.",
  linkedin_fetch_failed: "Login failed — we could not retrieve your LinkedIn profile. Please try again.",
  linkedin_exchange_failed: "Login failed — LinkedIn declined the request. Please try again.",
  linkedin_auth_denied: "Login cancelled — you declined the LinkedIn authorization request.",
  linkedin_server_misconfigured: "Login is currently unavailable. Please check back later.",
  server_misconfigured: "Login is currently unavailable. Please check back later.",
  session_error: "Login failed — there was a session problem. Please try again.",
  not_authenticated: "You must be logged in to perform that action.",
  email_linked_to_different_linkedin: "This email is already linked to a different LinkedIn account.",
  email_linked_to_different_google: "This email is already linked to a different Google account.",
  email_linked_to_different_discord: "This email is already linked to a different Discord account.",
};

const DEFAULT_AUTH_ERROR = "Login failed — please try again.";

export default function Home() {
  const isReduced = useReducedMotion();
  const rawSearch = useSearch();
  const [, navigate] = useLocation();
  const searchParams = rawSearch ? new URLSearchParams(rawSearch) : null;
  const initialTab: Tab = searchParams?.get("tab") === "opportunities" ? "opportunities" : "talent";

  const [tab, setTab] = useState<Tab>(initialTab);
  useDocumentTitle(tab === "opportunities" ? "Opportunities" : "Find Talent");
  const modeToggleRef = useRef<HTMLDivElement>(null);
  const [modeScrollState, setModeScrollState] = useState({ left: false, right: false });

  const [recruiterBannerDismissed, setRecruiterBannerDismissed] = useState<boolean>(() => {
    // ?showBanner=1 deep-link forces the banner visible and clears the stored preference
    if (searchParams?.get("showBanner") === "1") {
      try { localStorage.removeItem(RECRUITER_BANNER_KEY); } catch { /* non-fatal */ }
      return false;
    }
    try {
      return localStorage.getItem(RECRUITER_BANNER_KEY) === "true";
    } catch {
      return false;
    }
  });
  // Incremented to force-remount RecruiterValueProps and re-read localStorage after "Show intro"
  const [recruiterBannerResetKey, setRecruiterBannerResetKey] = useState(0);

  const handleShowRecruiterBanner = () => {
    try {
      localStorage.removeItem(RECRUITER_BANNER_KEY);
    } catch {
      // Non-fatal
    }
    setRecruiterBannerDismissed(false);
    setRecruiterBannerResetKey((k) => k + 1);
  };

  const [jobAlertsOpen, setJobAlertsOpen] = useState<boolean>(false);
  const manageAlertsRef = useRef<HTMLButtonElement | null>(null);
  const [opportunitiesHintDismissed, dismissOpportunitiesHint] = useHintDismissed(
    HINT_KEYS.opportunitiesManageAlerts,
  );
  const [alumnibannerDismissed, setAlumniBannerDismissed] = useState<boolean>(() =>
    readAlumniBannerDismissed(),
  );

  const handleDismissAlumniBanner = () => {
    try {
      localStorage.setItem(ALUMNI_BANNER_KEY, "true");
    } catch {
      // Non-fatal
    }
    setAlumniBannerDismissed(true);
  };

  const handleShowAlumniBanner = () => {
    try {
      localStorage.removeItem(ALUMNI_BANNER_KEY);
    } catch {
      // Non-fatal
    }
    setAlumniBannerDismissed(false);
  };

  const [search, setSearch] = useState(searchParams?.get("search") ?? "");
  const [discipline, setDiscipline] = useState<string>(
    searchParams?.get("discipline") ?? "all",
  );
  const [openToRelocation, setOpenToRelocation] = useState<boolean>(
    searchParams?.get("openToRelocation") === "true",
  );
  const [location, setLocation] = useState<string>(
    searchParams?.get("location") ?? "",
  );
  const [activelySeeking, setActivelySeeking] = useState<boolean>(
    searchParams?.get("activelySeeking") === "true",
  );
  const [talentFilterDrawerOpen, setTalentFilterDrawerOpen] = useState(false);

  const [jobSearch, setJobSearch] = useState<string>(
    searchParams?.get("q") ?? "",
  );
  const [jobCommunityOnly, setJobCommunityOnly] = useState<boolean>(
    searchParams?.get("communityOnly") === "true",
  );
  const [jobReferralOnly, setJobReferralOnly] = useState<boolean>(
    searchParams?.get("referralOnly") === "true",
  );
  const [jobExternalOnly, setJobExternalOnly] = useState<boolean>(
    searchParams?.get("externalOnly") === "true",
  );
  const [jobWorkModes, setJobWorkModes] = useState<string[]>(
    searchParams?.getAll("workMode") ?? [],
  );
  const [jobIndustryTags, setJobIndustryTags] = useState<string[]>(
    searchParams?.getAll("industryTag") ?? [],
  );
  const [jobLocation, setJobLocation] = useState<string>(
    searchParams?.get("jobLocation") ?? "",
  );
  const [jobFilterDrawerOpen, setJobFilterDrawerOpen] = useState(false);

  const [authError, setAuthError] = useState<string | null>(() => {
    const code = searchParams?.get("error");
    if (!code) return null;
    return AUTH_ERROR_MESSAGES[code] ?? DEFAULT_AUTH_ERROR;
  });

  // Strip ?error= from the URL immediately after capturing it so it doesn't persist on refresh
  useEffect(() => {
    const params = rawSearch ? new URLSearchParams(rawSearch) : null;
    if (params?.has("error")) {
      params.delete("error");
      const remaining = params.toString();
      navigate(remaining ? `/?${remaining}` : "/", { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep tab, openToRelocation, location, activelySeeking, search, and job filters in sync when nav links change the URL directly
  useEffect(() => {
    const params = rawSearch ? new URLSearchParams(rawSearch) : null;
    const tabFromUrl: Tab = params?.get("tab") === "opportunities" ? "opportunities" : "talent";
    setTab(tabFromUrl);
    setSearch(params?.get("search") ?? "");
    setDiscipline(params?.get("discipline") ?? "all");
    setOpenToRelocation(params?.get("openToRelocation") === "true");
    setLocation(params?.get("location") ?? "");
    setActivelySeeking(params?.get("activelySeeking") === "true");
    setJobSearch(params?.get("q") ?? "");
    setJobCommunityOnly(params?.get("communityOnly") === "true");
    setJobReferralOnly(params?.get("referralOnly") === "true");
    setJobExternalOnly(params?.get("externalOnly") === "true");
    setJobWorkModes(params?.getAll("workMode") ?? []);
    setJobIndustryTags(params?.getAll("industryTag") ?? []);
    setJobLocation(params?.get("jobLocation") ?? "");
  }, [rawSearch]);

  const highlightProfileId = searchParams ? parseInt(searchParams.get("highlightProfileId") ?? "", 10) : NaN;
  const highlightedId = Number.isNaN(highlightProfileId) ? null : highlightProfileId;
  const highlightRef = useRef<HTMLDivElement>(null);

  const { data: me, isLoading: meLoading } = useGetMe({ query: { queryKey: getGetMeQueryKey(), retry: false } });
  const { data: recruiterMe, isLoading: recruiterMeLoading } = useGetRecruiterMe({ query: { queryKey: getGetRecruiterMeQueryKey(), retry: false } });
  const authLoading = meLoading || recruiterMeLoading;
  const isAuthenticated = Boolean(me) || Boolean(recruiterMe);
  const isRecruiter = Boolean(recruiterMe);

  /**
   * True when the server-set `carry_hap` cookie is present, indicating this
   * browser previously authenticated as an alumni with a profile. Used to show
   * a neutral skeleton on the Opportunities tab during the /me loading window
   * instead of flashing the join CTA. Unauthenticated users never receive this
   * cookie so they always see the join CTA immediately.
   */
  const [mightBeAlumni] = useState<boolean>(readAlumniHintCookie);

  /**
   * True when the server-set `carry_hrp` cookie is present, indicating this
   * browser previously authenticated as a recruiter. Used together with
   * `mightBeAlumni` to suppress the unauthenticated hero flash for returning
   * recruiters during the brief session-loading window.
   */
  const [mightBeRecruiter] = useState<boolean>(readRecruiterHintCookie);

  /**
   * Unified hint — true when either the alumni or recruiter hint cookie is set.
   * Drives flash-suppression for the hero section and mode toggle during the
   * auth-loading window, covering both returning alumni and returning recruiters.
   */
  const mightBeAuthenticated = mightBeAlumni || mightBeRecruiter;

  const alumniProfileId = me?.profileId ?? null;
  const { data: alumniProfile, isLoading: alumniProfileLoading } = useGetProfile(
    alumniProfileId as number,
    {
      query: {
        queryKey: getGetProfileQueryKey(alumniProfileId as number),
        enabled: alumniProfileId != null,
        retry: false,
      },
    },
  );
  const isAlumniPanel = tab === "opportunities" && alumniProfileId != null;
  const isTalentPanel = tab === "talent" && me?.hasVerifiedRole === true && alumniProfileId != null;
  const isAlreadyListed =
    alumniProfileId != null &&
    alumniProfile?.verified === true &&
    alumniProfile?.isVisible === true;

  const { data: savedProfiles } = useListSavedProfiles({
    query: {
      queryKey: getListSavedProfilesQueryKey(),
      enabled: isRecruiter,
    },
  });

  const [recruiterSavedIds, setRecruiterSavedIds] = React.useState<Set<number>>(new Set());

  React.useEffect(() => {
    if (!savedProfiles) return;
    setRecruiterSavedIds(new Set(savedProfiles.map((e) => e.profileId)));
  }, [savedProfiles]);

  // If the user is authenticated, a spurious ?error=invalid_state from a
  // duplicate OAuth callback should not be shown — the login actually succeeded.
  useEffect(() => {
    if (!meLoading && me) {
      setAuthError(null);
    }
  }, [me, meLoading]);

  // If a new alumni landed here without a profile (e.g. the /onboarding redirect
  // was swallowed by a duplicate OAuth callback), send them to onboarding now.
  useEffect(() => {
    if (!meLoading && me && me.profileId == null) {
      navigate("/onboarding", { replace: true });
    }
  }, [me, meLoading, navigate]);

  const handleSaveToggle = (profileId: number, saved: boolean) => {
    if (isRecruiter) {
      setRecruiterSavedIds((prev) => {
        const next = new Set(prev);
        if (saved) next.add(profileId);
        else next.delete(profileId);
        return next;
      });
    } else {
      setAlumniSavedIds((prev) => {
        const next = new Set(prev);
        if (saved) next.add(profileId);
        else next.delete(profileId);
        return next;
      });
    }
  };

  const { data: stats, isLoading: statsLoading, isError: statsError } = useGetAlumniStats({ query: { queryKey: getGetAlumniStatsQueryKey() } });
  const { data: disciplines } = useListDisciplines({ query: { queryKey: getListDisciplinesQueryKey() } });
  const queryParams = {
    search: search || undefined,
    discipline: discipline !== "all" ? discipline : undefined,
    openToRelocation: openToRelocation || undefined,
    location: location.trim() || undefined,
    activelySeeking: activelySeeking || undefined,
  };

  const { data: profiles, isLoading: profilesLoading, isError: profilesError } = useListProfiles(queryParams, {
    query: { queryKey: getListProfilesQueryKey(queryParams), refetchInterval: 60_000 },
  });

  // Unfiltered list used only by the marquee ticker so chip-click filtering
  // does not collapse the title pool.
  const { data: allProfiles, isLoading: allProfilesLoading } = useListProfiles({}, {
    query: { queryKey: getListProfilesQueryKey({}), staleTime: 5 * 60 * 1000 },
  });

  const jobListParams = jobIndustryTags.length > 0 ? { industryTag: jobIndustryTags } : undefined;
  const { data: jobs, isLoading: jobsLoading, isError: jobsError } = useListJobs(jobListParams, {
    query: { queryKey: getListJobsQueryKey(jobListParams) },
  });

  const { data: activeJobTags } = useListActiveJobTags({
    query: { queryKey: getListActiveJobTagsQueryKey() },
  });

  const isVerifiedAlumni = me?.hasVerifiedRole === true;

  const jobAlertSubsQueryKey = getGetMyTagSubscriptionsQueryKey({ subscriptionType: "job" });
  const { data: jobAlertSubs = [] } = useGetMyTagSubscriptions(
    { subscriptionType: "job" },
    {
      query: {
        queryKey: jobAlertSubsQueryKey,
        retry: false,
        enabled: isVerifiedAlumni,
      },
    },
  );
  const jobAlertCount = jobAlertSubs.length;

  const { data: alumniSavedProfiles } = useListAlumniSavedProfiles({
    query: {
      queryKey: getListAlumniSavedProfilesQueryKey(),
      enabled: isVerifiedAlumni && !isRecruiter,
    },
  });

  const [alumniSavedIds, setAlumniSavedIds] = React.useState<Set<number>>(new Set());

  React.useEffect(() => {
    if (!alumniSavedProfiles) return;
    setAlumniSavedIds(new Set(alumniSavedProfiles.map((e) => e.profile.id)));
  }, [alumniSavedProfiles]);

  const [bookmarkHintLocalDismissed, setBookmarkHintLocalDismissed] = useState(false);
  const dismissBookmarkHintMutation = useDismissBookmarkHint();
  const dismissRecruiterBookmarkHintMutation = useDismissRecruiterBookmarkHint();

  const serverBookmarkHintDismissed = isRecruiter
    ? (recruiterMe?.bookmarkHintDismissed ?? false)
    : (me?.bookmarkHintDismissed ?? false);

  const showBookmarkHint =
    (isRecruiter || isVerifiedAlumni) && !serverBookmarkHintDismissed && !bookmarkHintLocalDismissed;

  const handleBookmarkHintDismiss = () => {
    setBookmarkHintLocalDismissed(true);
    if (isRecruiter) {
      dismissRecruiterBookmarkHintMutation.mutate();
    } else {
      dismissBookmarkHintMutation.mutate();
    }
  };

  const referralsAvailable = React.useMemo(() => {
    if (!jobs) return 0;
    return jobs.filter(isActionableReferral).length;
  }, [jobs]);

  const filteredJobs = React.useMemo(() => {
    if (!jobs) return [];
    return jobs.filter((job) => {
      const activePostTypes = [
        isVerifiedAlumni && jobCommunityOnly,
        isVerifiedAlumni && jobReferralOnly,
        isVerifiedAlumni && jobExternalOnly,
      ].filter(Boolean);
      if (activePostTypes.length > 0) {
        const isCommunity = job.authorId !== null;
        const isReferral = isActionableReferral(job);
        const isExternal = job.authorId === null && !isReferral;
        const matches =
          (isVerifiedAlumni && jobCommunityOnly && isCommunity) ||
          (isVerifiedAlumni && jobReferralOnly && isReferral) ||
          (isVerifiedAlumni && jobExternalOnly && isExternal);
        if (!matches) return false;
      }
      if (jobSearch.trim()) {
        const q = jobSearch.trim().toLowerCase();
        const industryText = Array.isArray(job.industryFocus) ? job.industryFocus.join(" ") : (job.industryFocus ?? "");
        const fields = [industryText, job.specificNeeds ?? "", job.jobTitle, job.companyName];
        if (!fields.some((f) => f?.toLowerCase().includes(q))) return false;
      }
      if (jobWorkModes.length > 0) {
        const modes: string[] = job.workModes ?? [];
        if (!jobWorkModes.some((m) => modes.includes(m))) return false;
      }
      if (jobLocation.trim()) {
        const q = jobLocation.trim().toLowerCase();
        const locs: string[] = job.locations ?? [];
        if (!locs.some((l) => l.toLowerCase().includes(q))) return false;
      }
      if (jobIndustryTags.length > 0) {
        const industries: string[] = Array.isArray(job.industryFocus)
          ? job.industryFocus
          : job.industryFocus
            ? [job.industryFocus]
            : [];
        if (
          !jobIndustryTags.some((t) =>
            industries.some((i) => i.toLowerCase().includes(t.toLowerCase())),
          )
        )
          return false;
      }
      return true;
    });
  }, [jobs, jobSearch, jobCommunityOnly, jobReferralOnly, jobExternalOnly, isVerifiedAlumni, jobWorkModes, jobLocation, jobIndustryTags]);

  const hasActiveJobFilters = jobSearch.trim() !== "" || (isVerifiedAlumni && (jobCommunityOnly || jobReferralOnly || jobExternalOnly)) || jobWorkModes.length > 0 || jobIndustryTags.length > 0 || jobLocation.trim() !== "";

  const jobFilterCount =
    jobWorkModes.length +
    (isVerifiedAlumni
      ? [jobCommunityOnly, jobReferralOnly, jobExternalOnly].filter(Boolean).length
      : 0) +
    (jobLocation.trim() ? 1 : 0) +
    jobIndustryTags.length;

  useEffect(() => {
    if (!highlightedId || !profiles?.length) return;
    const timer = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 600);
    return () => clearTimeout(timer);
  }, [highlightedId, profiles]);

  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (scrollRestoredRef.current) return;
    if (!profiles) return;
    try {
      const saved = sessionStorage.getItem(HOME_SCROLL_KEY);
      if (saved !== null) {
        const y = parseInt(saved, 10);
        sessionStorage.removeItem(HOME_SCROLL_KEY);
        if (!Number.isNaN(y) && y > 0) {
          scrollRestoredRef.current = true;
          requestAnimationFrame(() => {
            window.scrollTo({ top: y, behavior: "instant" });
          });
        }
      }
    } catch {
    }
  }, [profiles]);

  const handleProfileCardNavigate = useCallback(() => {
    try {
      sessionStorage.setItem(HOME_SCROLL_KEY, String(window.scrollY));
    } catch {
    }
  }, []);

  const jobsScrollRestoredRef = useRef(false);
  useEffect(() => {
    if (jobsScrollRestoredRef.current) return;
    if (tab !== "opportunities") return;
    if (!jobs) return;
    try {
      const saved = sessionStorage.getItem(JOBS_SCROLL_KEY);
      if (saved !== null) {
        const y = parseInt(saved, 10);
        sessionStorage.removeItem(JOBS_SCROLL_KEY);
        if (!Number.isNaN(y) && y > 0) {
          jobsScrollRestoredRef.current = true;
          requestAnimationFrame(() => {
            window.scrollTo({ top: y, behavior: "instant" });
          });
        }
      }
    } catch {
    }
  }, [jobs, tab]);

  useEffect(() => {
    const el = modeToggleRef.current;
    if (!el) return;
    const update = () => {
      setModeScrollState({
        left: el.scrollLeft > 4,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);

  const handleJobCardNavigate = useCallback(() => {
    try {
      sessionStorage.setItem(JOBS_SCROLL_KEY, String(window.scrollY));
    } catch {
    }
  }, []);

  function buildUrl(overrides: { tab?: Tab; search?: string; discipline?: string; openToRelocation?: boolean; location?: string; activelySeeking?: boolean; jobSearch?: string; jobCommunityOnly?: boolean; jobReferralOnly?: boolean; jobExternalOnly?: boolean; jobWorkModes?: string[]; jobIndustryTags?: string[]; jobLocation?: string }) {
    const resolvedTab = overrides.tab ?? tab;
    const resolvedSearch = "search" in overrides ? (overrides.search ?? "") : search;
    const resolvedDiscipline = "discipline" in overrides ? (overrides.discipline ?? "all") : discipline;
    const resolvedRelocation = "openToRelocation" in overrides ? overrides.openToRelocation : openToRelocation;
    const resolvedLocation = "location" in overrides ? (overrides.location ?? "") : location;
    const resolvedActivelySeeking = "activelySeeking" in overrides ? overrides.activelySeeking : activelySeeking;
    const resolvedJobSearch = "jobSearch" in overrides ? (overrides.jobSearch ?? "") : jobSearch;
    const resolvedJobCommunityOnly = "jobCommunityOnly" in overrides ? overrides.jobCommunityOnly : jobCommunityOnly;
    const resolvedJobReferralOnly = "jobReferralOnly" in overrides ? overrides.jobReferralOnly : jobReferralOnly;
    const resolvedJobExternalOnly = "jobExternalOnly" in overrides ? overrides.jobExternalOnly : jobExternalOnly;
    const resolvedJobWorkModes = "jobWorkModes" in overrides ? (overrides.jobWorkModes ?? []) : jobWorkModes;
    const resolvedJobIndustryTags = "jobIndustryTags" in overrides ? (overrides.jobIndustryTags ?? []) : jobIndustryTags;
    const resolvedJobLocation = "jobLocation" in overrides ? (overrides.jobLocation ?? "") : jobLocation;
    const parts: string[] = [];
    if (resolvedTab === "opportunities") parts.push("tab=opportunities");
    if (resolvedSearch?.trim()) parts.push(`search=${encodeURIComponent(resolvedSearch.trim())}`);
    if (resolvedDiscipline && resolvedDiscipline !== "all") parts.push(`discipline=${encodeURIComponent(resolvedDiscipline)}`);
    if (resolvedRelocation) parts.push("openToRelocation=true");
    if (resolvedLocation?.trim()) parts.push(`location=${encodeURIComponent(resolvedLocation.trim())}`);
    if (resolvedActivelySeeking) parts.push("activelySeeking=true");
    if (resolvedJobSearch?.trim()) parts.push(`q=${encodeURIComponent(resolvedJobSearch.trim())}`);
    if (resolvedJobCommunityOnly) parts.push("communityOnly=true");
    if (resolvedJobReferralOnly) parts.push("referralOnly=true");
    if (resolvedJobExternalOnly) parts.push("externalOnly=true");
    for (const m of resolvedJobWorkModes) {
      parts.push(`workMode=${encodeURIComponent(m)}`);
    }
    for (const t of resolvedJobIndustryTags) {
      parts.push(`industryTag=${encodeURIComponent(t)}`);
    }
    if (resolvedJobLocation?.trim()) parts.push(`jobLocation=${encodeURIComponent(resolvedJobLocation.trim())}`);
    return parts.length > 0 ? `/?${parts.join("&")}` : "/";
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    navigate(buildUrl({ search: value }), { replace: true });
  }

  function handleJobSearchChange(value: string) {
    setJobSearch(value);
    navigate(buildUrl({ jobSearch: value }), { replace: true });
  }

  function handleJobCommunityOnlyChange(checked: boolean) {
    setJobCommunityOnly(checked);
    navigate(buildUrl({ jobCommunityOnly: checked }), { replace: true });
  }

  function handleJobReferralOnlyChange(checked: boolean) {
    setJobReferralOnly(checked);
    navigate(buildUrl({ jobReferralOnly: checked }), { replace: true });
  }

  function handleJobExternalOnlyChange(checked: boolean) {
    setJobExternalOnly(checked);
    navigate(buildUrl({ jobExternalOnly: checked }), { replace: true });
  }

  function handleJobWorkModeToggle(mode: string) {
    const next = jobWorkModes.includes(mode)
      ? jobWorkModes.filter((m) => m !== mode)
      : [...jobWorkModes, mode];
    setJobWorkModes(next);
    navigate(buildUrl({ jobWorkModes: next }), { replace: true });
  }

  function handleJobLocationChange(value: string) {
    setJobLocation(value);
    navigate(buildUrl({ jobLocation: value }), { replace: true });
  }

  function clearJobFilters() {
    setJobSearch("");
    setJobCommunityOnly(false);
    setJobReferralOnly(false);
    setJobExternalOnly(false);
    setJobWorkModes([]);
    setJobIndustryTags([]);
    setJobLocation("");
    navigate(buildUrl({ jobSearch: "", jobCommunityOnly: false, jobReferralOnly: false, jobExternalOnly: false, jobWorkModes: [], jobIndustryTags: [], jobLocation: "" }), { replace: true });
  }

  function handleJobIndustryTagToggle(tag: string) {
    const next = jobIndustryTags.includes(tag)
      ? jobIndustryTags.filter((t) => t !== tag)
      : [...jobIndustryTags, tag];
    setJobIndustryTags(next);
    navigate(buildUrl({ jobIndustryTags: next }), { replace: true });
  }

  const handleTabChange = (next: Tab) => {
    setTab(next);
    navigate(buildUrl({ tab: next }), { replace: true });
  };

  const handleOpenToRelocationToggle = () => {
    const next = !openToRelocation;
    setOpenToRelocation(next);
    navigate(buildUrl({ openToRelocation: next }), { replace: true });
  };

  const handleActivelySeekingToggle = () => {
    const next = !activelySeeking;
    setActivelySeeking(next);
    navigate(buildUrl({ activelySeeking: next }), { replace: true });
  };

  const handleDisciplineChange = (val: string) => {
    setDiscipline(val);
    navigate(buildUrl({ discipline: val }), { replace: true });
  };

  const handleLocationChange = (val: string) => {
    setLocation(val);
    navigate(buildUrl({ location: val }), { replace: true });
  };

  const hasActiveFilters = !!(
    search || discipline !== "all" || location.trim() || openToRelocation || activelySeeking
  );

  const talentDrawerFilterCount =
    (discipline !== "all" ? 1 : 0) +
    (location.trim() ? 1 : 0) +
    (openToRelocation ? 1 : 0) +
    (activelySeeking ? 1 : 0);

  const handleClearFilters = () => {
    setSearch("");
    setDiscipline("all");
    setLocation("");
    setOpenToRelocation(false);
    setActivelySeeking(false);
    navigate(buildUrl({ search: "", discipline: "all", openToRelocation: false, activelySeeking: false, location: "" }), { replace: true });
  };

  const handleGetListed = () => {
    if (!me) {
      navigate("/login");
    } else if (alumniProfileId == null) {
      navigate("/onboarding");
    } else {
      navigate("/dashboard");
    }
  };

  return (
    <div className="w-full flex flex-col">
      {/* Auth Error Banner */}
      <AnimatePresence>
        {authError && (
          <motion.div
            initial={isReduced ? false : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={isReduced ? {} : { opacity: 0, y: -8 }}
            transition={{ duration: isReduced ? 0 : 0.25 }}
            className="w-full bg-red-950/80 border-b border-red-800/60 px-4 py-3"
          >
            <div className="container mx-auto max-w-6xl flex items-center gap-3">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
              <p className="flex-1 text-sm text-red-300 font-sans">{authError}</p>
              <button
                onClick={() => setAuthError(null)}
                className="text-red-400 hover:text-red-200 transition-colors shrink-0"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className={`relative ${isAuthenticated || (authLoading && mightBeAuthenticated) ? "pt-5 pb-4" : "pt-[86px] pb-20"} px-4 flex flex-col items-center justify-center overflow-hidden`}>
        {!(isAuthenticated || (authLoading && mightBeAuthenticated)) && (
          <>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-[0.03] pointer-events-none" />
          </>
        )}

        {/* Mode Toggle — hidden for authenticated users and during auth-loading for returning users */}
        {!isAuthenticated && !(authLoading && mightBeAuthenticated) && (
          <div className="relative z-10 mb-12 w-fit max-w-full">
            {modeScrollState.left && (
              <div
                className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 rounded-l-full"
                style={{ background: "linear-gradient(to right, rgb(9 9 11 / 0.9), transparent)" }}
              />
            )}
            {modeScrollState.right && (
              <div
                className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 rounded-r-full"
                style={{ background: "linear-gradient(to left, rgb(9 9 11 / 0.9), transparent)" }}
              />
            )}
            <div ref={modeToggleRef} className="flex p-1 bg-zinc-900/80 backdrop-blur border border-white/10 rounded-full max-w-full overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <button
                onClick={() => handleTabChange("talent")}
                className={`relative px-6 py-2 text-sm font-semibold rounded-full transition-colors z-10 shrink-0 ${tab === "talent" ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}
              >
                {tab === "talent" && (
                  <motion.div
                    layoutId="toggle-bg"
                    className="absolute inset-0 bg-zinc-800 rounded-full shadow-sm"
                    transition={isReduced ? { duration: 0 } : { type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <span className="relative z-20">Find Talent</span>
              </button>
              <button
                onClick={() => handleTabChange("opportunities")}
                className={`relative px-6 py-2 text-sm font-semibold rounded-full transition-colors z-10 shrink-0 ${tab === "opportunities" ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}
              >
                {tab === "opportunities" && (
                  <motion.div
                    layoutId="toggle-bg"
                    className="absolute inset-0 bg-zinc-800 rounded-full shadow-sm"
                    transition={isReduced ? { duration: 0 } : { type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <span className="relative z-20">Find Opportunities</span>
              </button>
            </div>
          </div>
        )}

        {/* Hero Content — hidden for authenticated users and during auth-loading for returning users */}
        {!isAuthenticated && !(authLoading && mightBeAuthenticated) && (
          <div className="relative z-10 flex flex-col items-center max-w-3xl text-center min-h-[220px]">
            <AnimatePresence mode="wait">
              {tab === "talent" ? (
                <motion.div
                  key="talent"
                  initial={isReduced ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={isReduced ? {} : { opacity: 0, y: -10 }}
                  transition={{ duration: isReduced ? 0 : 0.3 }}
                  className="flex flex-col items-center"
                >
                  <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-6 text-white drop-shadow-sm uppercase">
                    {DIRECTORY_HERO.headlinePlain} <br />
                    <span className="text-primary">{DIRECTORY_HERO.headlineAccent}</span>
                  </h1>
                  <p className="text-lg md:text-xl text-zinc-400 mb-8 max-w-2xl font-sans">
                    {DIRECTORY_HERO.subhead}
                  </p>
                  <Link
                    href="/jobs/new"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-primary hover:bg-primary/90 text-white font-bold tracking-wide uppercase px-8 h-14 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Post an Opportunity
                  </Link>
                </motion.div>
              ) : meLoading && mightBeAlumni ? (
                <motion.div
                  key="opportunities-loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex flex-col items-center w-full max-w-2xl"
                  aria-hidden="true"
                >
                  <div className="h-16 md:h-24 w-3/4 rounded-lg bg-white/[0.04] animate-pulse mb-4" />
                  <div className="h-16 md:h-24 w-1/2 rounded-lg bg-white/[0.04] animate-pulse mb-8" />
                  <div className="h-5 w-2/3 rounded bg-white/[0.04] animate-pulse mb-3" />
                  <div className="h-5 w-1/2 rounded bg-white/[0.04] animate-pulse mb-8" />
                  <div className="h-14 w-56 rounded-md bg-white/[0.04] animate-pulse" />
                </motion.div>
              ) : (
                <motion.div
                  key="opportunities"
                  initial={isReduced ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={isReduced ? {} : { opacity: 0, y: -10 }}
                  transition={{ duration: isReduced ? 0 : 0.3 }}
                  className="flex flex-col items-center"
                >
                  <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-6 text-white drop-shadow-sm uppercase">
                    {OPPORTUNITIES_HERO.headlinePlain} <br />
                    <span className="text-primary">{OPPORTUNITIES_HERO.headlineAccent}</span>
                  </h1>
                  <p className="text-lg md:text-xl text-zinc-400 mb-8 max-w-2xl font-sans">
                    {OPPORTUNITIES_HERO.subhead}
                  </p>
                  <Link
                    href="/login"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-primary hover:bg-primary/90 text-white font-bold tracking-wide uppercase px-8 h-14 text-sm"
                  >
                    Create Your Profile
                  </Link>
                  <p className="text-sm text-zinc-500 font-sans mt-3">
                    Sign in to get started.
                  </p>
                  <p className="text-sm text-zinc-500 font-sans mt-1">
                    Already have an account?{" "}
                    <Link
                      href="/login"
                      className="inline-flex items-center gap-1 text-zinc-400 hover:text-zinc-200 transition-colors underline underline-offset-2"
                    >
                      Sign in here.
                    </Link>
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </section>

      {/* Value-Prop Banners */}
      <AnimatePresence mode="wait">
        {tab === "opportunities" && !isAlumniPanel && !authLoading && me?.hasVerifiedRole !== true ? (
          <AlumniValueProps
            key="alumni-props"
            dismissed={alumnibannerDismissed}
            onDismiss={handleDismissAlumniBanner}
          />
        ) : tab === "opportunities" || isTalentPanel ? null : (
          <RecruiterValueProps
            key={`recruiter-props-${recruiterBannerResetKey}`}
            onDismissed={() => setRecruiterBannerDismissed(true)}
          />
        )}
      </AnimatePresence>

      <DynamicHeader
        tab={tab}
        stats={stats}
        statsLoading={statsLoading}
        statsError={statsError}
        profiles={allProfiles}
        profilesLoading={allProfilesLoading}
        onTitleFilter={(title) => {
          const nextTab = tab !== "talent" ? "talent" : tab;
          setSearch(title);
          setTab(nextTab);
          navigate(buildUrl({ search: title, tab: nextTab }), { replace: true });
        }}
        isVerifiedAlumni={isVerifiedAlumni}
        referralsAvailable={referralsAvailable}
      />

      {/* Content Section */}
      <section className="py-16 px-4 container mx-auto max-w-6xl relative z-10 flex-1">
        <AnimatePresence mode="wait">
          {tab === "talent" ? (
            <motion.div
              key="talent-section"
              initial={isReduced ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={isReduced ? {} : { opacity: 0, y: -8 }}
              transition={{ duration: isReduced ? 0 : 0.25 }}
            >
              <div className="flex flex-col gap-4 mb-8">
                {/* Section heading row with CTA */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="text-2xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
                      Alumni Directory
                      <div className="h-px bg-primary w-12 mt-1" />
                      {recruiterBannerDismissed && (
                      <button
                        onClick={handleShowRecruiterBanner}
                        className="text-xs font-normal normal-case tracking-normal text-zinc-600 hover:text-zinc-400 transition-colors font-sans ml-1"
                      >
                        Show intro
                      </button>
                    )}
                  </h2>
                    {me?.displayName && (
                      <p className="text-sm text-zinc-500 font-sans mt-0.5">Welcome back, {me.displayName}</p>
                    )}
                  </div>

                  {/* CTA buttons anchored to the right of the heading */}
                  <div className="flex items-center gap-2 shrink-0">
                    {!meLoading && !(alumniProfileId != null && alumniProfileLoading) && !isAlreadyListed && (
                      <Button
                        variant="outline"
                        size="sm"
                        className={
                          !isAuthenticated
                            ? "bg-primary hover:bg-primary/90 text-white border-primary font-semibold uppercase tracking-wide font-sans inline-flex items-center gap-1.5"
                            : "border-white/20 text-zinc-300 hover:border-primary hover:text-primary bg-transparent font-semibold uppercase tracking-wide font-sans"
                        }
                        onClick={handleGetListed}
                      >
                        Create Your Profile
                      </Button>
                    )}

                    {!meLoading && isAlreadyListed && alumniProfileId != null && (
                      <Link href={`/profile/${alumniProfileId}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-primary text-primary hover:bg-primary/10 bg-transparent font-semibold uppercase tracking-wide font-sans"
                        >
                          My Profile
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>

                {/* Filter bar */}
                <TalentSearchBar
                  search={search}
                  onSearchChange={handleSearchChange}
                  disciplines={disciplines ?? []}
                  discipline={discipline}
                  onDisciplineChange={handleDisciplineChange}
                  filterCount={talentDrawerFilterCount}
                  onOpenFilters={() => setTalentFilterDrawerOpen(true)}
                />
              </div>

              <TalentFilterDrawer
                open={talentFilterDrawerOpen}
                onOpenChange={setTalentFilterDrawerOpen}
                disciplines={disciplines ?? []}
                discipline={discipline}
                onDisciplineChange={handleDisciplineChange}
                location={location}
                onLocationChange={handleLocationChange}
                openToRelocation={openToRelocation}
                onOpenToRelocationToggle={handleOpenToRelocationToggle}
                activelySeeking={activelySeeking}
                onActivelySeekingToggle={handleActivelySeekingToggle}
                onClearAll={handleClearFilters}
              />

              {isAuthenticated && stats && (
                <div className="flex items-center justify-between gap-4 mb-6">
                  <p className="text-xs text-zinc-500 font-sans tracking-wide">
                    Over {stats.cumulativeExperienceYears.toLocaleString()} collective years of Riot experience across the network.
                  </p>
                  {isVerifiedAlumni && (
                    <Link href="/dashboard#invite" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors font-sans whitespace-nowrap shrink-0">
                      Know an alum who isn&apos;t here yet?
                    </Link>
                  )}
                </div>
              )}

              {profilesLoading ? (
                <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="break-inside-avoid mb-6 bg-zinc-900/60 border border-white/10 rounded-xl p-6 flex flex-col">
                      <div className="flex items-start gap-4 mb-5">
                        <Skeleton className="w-16 h-16 rounded-lg shrink-0 bg-white/5" />
                        <div className="flex-1 min-w-0 space-y-2 pt-0.5">
                          <Skeleton className="h-5 w-3/4 bg-white/5" />
                          <Skeleton className="h-4 w-1/2 bg-white/5" />
                          <div className="flex items-center gap-3 mt-1">
                            <Skeleton className="h-3 w-20 bg-white/5" />
                            <Skeleton className="h-3 w-24 bg-white/5" />
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 space-y-4">
                        <div className="space-y-1.5">
                          <Skeleton className="h-3 w-24 bg-white/5" />
                          <Skeleton className="h-4 w-full bg-white/5" />
                        </div>
                        <div className="space-y-1.5">
                          <Skeleton className="h-3 w-16 bg-white/5" />
                          <Skeleton className="h-4 w-full bg-white/5" />
                          <Skeleton className="h-4 w-5/6 bg-white/5" />
                        </div>
                      </div>
                      <div className="mt-5 pt-5 border-t border-white/5 flex items-center justify-between">
                        <Skeleton className="h-5 w-24 rounded bg-white/5" />
                      </div>
                      <div className="mt-3">
                        <Skeleton className="h-8 w-full rounded-md bg-white/5" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : profilesError ? (
                <div className="py-20 text-center flex flex-col items-center justify-center border border-white/5 rounded-2xl bg-zinc-900/20 border-dashed">
                  <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
                    <Search className="w-8 h-8 text-zinc-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Unable to load talent</h3>
                  <p className="text-zinc-500 max-w-md font-sans">
                    Something went wrong while fetching the directory. Please refresh the page to try again.
                  </p>
                </div>
              ) : profiles && profiles.length > 0 ? (
                <div
                  key={`profiles-${search}-${discipline}-${location}-${openToRelocation ? 1 : 0}-${activelySeeking ? 1 : 0}`}
                  className="columns-1 md:columns-2 lg:columns-3 gap-6"
                >
                  {profiles.map((profile, idx) => {
                    const isHighlighted = profile.id === highlightedId;
                    return (
                      <motion.div
                        key={profile.id}
                        ref={isHighlighted ? highlightRef : null}
                        initial={isReduced ? false : { opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: isReduced ? 0 : 0.35,
                          delay: isReduced ? 0 : Math.min(idx * 0.05, 0.4),
                          ease: [0.21, 0.47, 0.32, 0.98],
                        }}
                        className={
                          isHighlighted
                            ? "break-inside-avoid mb-6 rounded-xl ring-2 ring-primary shadow-[0_0_48px_-8px_rgba(232,24,74,0.55)]"
                            : "break-inside-avoid mb-6"
                        }
                        onClick={handleProfileCardNavigate}
                      >
                        <ProfileCard
                          profile={profile}
                          viewerIsVerified={me?.hasVerifiedRole === true}
                          viewerProfileId={me?.profileId ?? null}
                          viewerHasDiscord={me?.discordConnected === true}
                          recruiterSavedIds={isRecruiter ? recruiterSavedIds : undefined}
                          alumniSavedIds={!isRecruiter && isVerifiedAlumni ? alumniSavedIds : undefined}
                          onSaveToggle={isRecruiter || isVerifiedAlumni ? handleSaveToggle : undefined}
                          onDisciplineClick={(d) => {
                            setDiscipline(d);
                            if (tab !== "talent") handleTabChange("talent");
                          }}
                          onLocationClick={(loc) => {
                            handleLocationChange(loc);
                            if (tab !== "talent") handleTabChange("talent");
                          }}
                          showBookmarkHint={showBookmarkHint && idx === 0}
                          onBookmarkHintDismiss={handleBookmarkHintDismiss}
                        />
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-20 text-center flex flex-col items-center justify-center border border-white/5 rounded-2xl bg-zinc-900/20 border-dashed">
                  <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
                    <Search className="w-8 h-8 text-zinc-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">No talent found</h3>
                  <p className="text-zinc-500 max-w-md font-sans">
                    Try adjusting your search filters or clear them to see all available members.
                  </p>
                  {hasActiveFilters && (
                    <Button
                      variant="outline"
                      className="mt-6 border-white/10 hover:bg-white/5 font-sans"
                      onClick={handleClearFilters}
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="opportunities-section"
              initial={isReduced ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={isReduced ? {} : { opacity: 0, y: -8 }}
              transition={{ duration: isReduced ? 0 : 0.25 }}
            >
              <div className="flex flex-col md:flex-row justify-between items-end md:items-center mb-4 gap-4">
                <div>
                  <h2 className="text-2xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
                    Open Opportunities
                    <div className="h-px bg-primary w-12 hidden md:block mt-1" />
                    {alumnibannerDismissed && !isAlumniPanel && me?.hasVerifiedRole !== true && (
                      <button
                        onClick={handleShowAlumniBanner}
                        className="text-xs font-normal normal-case tracking-normal text-zinc-600 hover:text-zinc-400 transition-colors font-sans ml-1"
                      >
                        Show intro
                      </button>
                    )}
                  </h2>
                  {me?.displayName && (
                    <p className="text-sm text-zinc-500 font-sans mt-0.5">Welcome back, {me.displayName}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isVerifiedAlumni && (
                    <Button
                      ref={manageAlertsRef}
                      type="button"
                      variant="outline"
                      onClick={() => setJobAlertsOpen(true)}
                      className="relative h-9 px-3 border-white/10 bg-zinc-900/50 hover:bg-zinc-800 text-zinc-300 hover:text-white gap-2"
                      aria-label="Manage job alerts"
                    >
                      <Bell className="w-3.5 h-3.5" />
                      <span className="text-xs font-bold uppercase tracking-wide">Manage Alerts</span>
                      {jobAlertCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white leading-none">
                          {jobAlertCount}
                        </span>
                      )}
                    </Button>
                  )}
                  <Link
                    href="/jobs/new"
                    className="inline-flex items-center gap-2 px-4 h-9 rounded-md bg-primary hover:bg-primary/90 text-white text-xs font-bold uppercase tracking-wide transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Post Opportunity
                  </Link>
                </div>
              </div>

              <AnchoredHintBubble
                show={isVerifiedAlumni && !opportunitiesHintDismissed}
                onDismiss={dismissOpportunitiesHint}
                anchorRef={manageAlertsRef}
                dismissAriaLabel="Dismiss manage alerts hint"
              >
                <p>
                  <span className="font-semibold text-white">Stay in the loop on new opportunities.</span>{" "}
                  Use Manage Alerts to tell us which roles you want to hear about, and we&apos;ll
                  email you when matching opportunities are posted.
                </p>
              </AnchoredHintBubble>

              {/* Job board search & filter — verified alumni only */}
              {isVerifiedAlumni && (
                <>
                  <div className="mb-4">
                    <JobSearchBar
                      jobSearch={jobSearch}
                      onJobSearchChange={handleJobSearchChange}
                      activeJobTags={activeJobTags}
                      jobIndustryTags={jobIndustryTags}
                      onIndustryTagToggle={handleJobIndustryTagToggle}
                      filterCount={jobFilterCount}
                      onOpenFilters={() => setJobFilterDrawerOpen(true)}
                    />
                  </div>
                  <JobFilterDrawer
                    open={jobFilterDrawerOpen}
                    onOpenChange={setJobFilterDrawerOpen}
                    isVerifiedAlumni={isVerifiedAlumni}
                    jobCommunityOnly={jobCommunityOnly}
                    onCommunityOnlyChange={handleJobCommunityOnlyChange}
                    jobReferralOnly={jobReferralOnly}
                    onReferralOnlyChange={handleJobReferralOnlyChange}
                    jobExternalOnly={jobExternalOnly}
                    onExternalOnlyChange={handleJobExternalOnlyChange}
                    jobWorkModes={jobWorkModes}
                    onWorkModeToggle={handleJobWorkModeToggle}
                    jobLocation={jobLocation}
                    onLocationChange={handleJobLocationChange}
                    jobIndustryTags={jobIndustryTags}
                    onIndustryTagToggle={handleJobIndustryTagToggle}
                    activeJobTags={activeJobTags}
                    onClearAll={clearJobFilters}
                  />
                </>
              )}

              {me?.hasVerifiedRole === true && <JobSafetyBanner />}

              {me?.hasVerifiedRole !== true && (
                <div className="flex items-center gap-3 mb-6 px-4 py-3 rounded-lg bg-zinc-900/60 border border-white/10 text-zinc-400 font-sans text-sm">
                  <Info className="w-4 h-4 text-zinc-500 shrink-0" />
                  <span>
                    {me ? (
                      "Verify your alumni status to view application links and contact recruiters."
                    ) : (
                      <>
                        <Link
                          href="/login?returnTo=%2F%3Ftab%3Dopportunities"
                          className="inline-flex items-center gap-1 text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
                        >
                          Sign in
                        </Link>{" "}
                        to view application links and contact recruiters.
                      </>
                    )}
                  </span>
                </div>
              )}

              {jobsLoading ? (
                <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="break-inside-avoid mb-6 bg-zinc-900/40 border border-white/5 rounded-xl p-6 h-[260px] flex flex-col">
                      <div className="flex items-start gap-3 mb-4">
                        <Skeleton className="w-10 h-10 rounded-lg bg-white/5" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-5 w-2/3 bg-white/5" />
                          <Skeleton className="h-3 w-1/3 bg-white/5" />
                        </div>
                      </div>
                      <div className="space-y-3 flex-1">
                        <Skeleton className="h-4 w-full bg-white/5" />
                        <Skeleton className="h-4 w-4/5 bg-white/5" />
                        <Skeleton className="h-4 w-3/5 bg-white/5" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : jobsError ? (
                <div className="py-20 text-center flex flex-col items-center justify-center border border-white/5 rounded-2xl bg-zinc-900/20 border-dashed">
                  <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
                    <Plus className="w-8 h-8 text-zinc-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Unable to load opportunities</h3>
                  <p className="text-zinc-500 max-w-md font-sans">
                    Something went wrong while fetching opportunities. Please refresh the page to try again.
                  </p>
                </div>
              ) : filteredJobs.length > 0 ? (
                <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
                  {filteredJobs.map((job, idx) => (
                    <motion.div
                      key={job.id}
                      className="break-inside-avoid mb-6"
                      initial={isReduced ? false : { opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: isReduced ? 0 : 0.35,
                        delay: isReduced ? 0 : Math.min(idx * 0.05, 0.4),
                        ease: [0.21, 0.47, 0.32, 0.98],
                      }}
                    >
                      <JobCard job={job} onNavigate={handleJobCardNavigate} />
                    </motion.div>
                  ))}
                </div>
              ) : hasActiveJobFilters ? (
                <div className="py-20 text-center flex flex-col items-center justify-center border border-white/5 rounded-2xl bg-zinc-900/20 border-dashed">
                  <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
                    <Search className="w-8 h-8 text-zinc-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">No opportunities match your filters</h3>
                  <p className="text-zinc-500 max-w-md font-sans mb-6">
                    Try adjusting your search or clearing the filters to see all open opportunities.
                  </p>
                  <button
                    onClick={clearJobFilters}
                    className="inline-flex items-center gap-2 px-6 h-10 rounded-md border border-white/10 hover:border-white/20 text-zinc-300 hover:text-white text-sm font-bold uppercase tracking-wide transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="py-20 text-center flex flex-col items-center justify-center border border-white/5 rounded-2xl bg-zinc-900/20 border-dashed">
                  <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
                    <Plus className="w-8 h-8 text-zinc-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">No opportunities posted yet</h3>
                  <p className="text-zinc-500 max-w-md font-sans mb-6">
                    Be the first to post an opportunity and connect with world-class gaming industry veterans.
                  </p>
                  <Link
                    href="/jobs/new"
                    className="inline-flex items-center gap-2 px-6 h-10 rounded-md bg-primary hover:bg-primary/90 text-white text-sm font-bold uppercase tracking-wide transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Post Opportunity
                  </Link>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      <Dialog open={jobAlertsOpen} onOpenChange={setJobAlertsOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-display uppercase tracking-tight">
              Job Alerts
            </DialogTitle>
            <DialogDescription className="text-zinc-400 font-sans">
              Select disciplines and titles you want to be notified about when a new matching opportunity is posted.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <TagSubscriptionManager
              subscriptionType="job"
              onSaveSuccess={() => setJobAlertsOpen(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
